import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface AssignedCompany {
  employerCompanyId: string;
  companyName: string | null;
}

interface AdminContextState {
  selectedCompanyId: string | null;
  selectedCompanyName: string | null;
  assignedCompanies: AssignedCompany[];
  loaded: boolean;
}

const SS_KEY = 'adminContext';

function loadFromSession(): Partial<AdminContextState> {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (raw) return JSON.parse(raw) as Partial<AdminContextState>;
  } catch { /* ignore */ }
  return {};
}

function saveToSession(state: AdminContextState) {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify({
      selectedCompanyId: state.selectedCompanyId,
      selectedCompanyName: state.selectedCompanyName,
    }));
  } catch { /* ignore */ }
}

const persisted = loadFromSession();

const initialState: AdminContextState = {
  selectedCompanyId: persisted.selectedCompanyId ?? null,
  selectedCompanyName: persisted.selectedCompanyName ?? null,
  assignedCompanies: [],
  loaded: false,
};

const adminContextSlice = createSlice({
  name: 'adminContext',
  initialState,
  reducers: {
    setAssignedCompanies(state, action: PayloadAction<AssignedCompany[]>) {
      state.assignedCompanies = action.payload;
      state.loaded = true;
      // If previously selected company is no longer in the list, clear it
      if (
        state.selectedCompanyId &&
        !action.payload.some(c => c.employerCompanyId === state.selectedCompanyId)
      ) {
        state.selectedCompanyId = null;
        state.selectedCompanyName = null;
        saveToSession(state);
      }
    },
    setSelectedCompany(state, action: PayloadAction<{ id: string; name: string | null }>) {
      state.selectedCompanyId = action.payload.id;
      state.selectedCompanyName = action.payload.name;
      saveToSession(state);
    },
    clearSelectedCompany(state) {
      state.selectedCompanyId = null;
      state.selectedCompanyName = null;
      saveToSession(state);
    },
    clearAdminContext(state) {
      state.selectedCompanyId = null;
      state.selectedCompanyName = null;
      state.assignedCompanies = [];
      state.loaded = false;
      try { sessionStorage.removeItem(SS_KEY); } catch { /* ignore */ }
    },
  },
});

export const {
  setAssignedCompanies,
  setSelectedCompany,
  clearSelectedCompany,
  clearAdminContext,
} = adminContextSlice.actions;

export default adminContextSlice.reducer;
